import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useUiLocale } from "../context/UiLocaleContext";
import { useTranslation } from "../i18n/useTranslation";

const EXACT_PATHS = new Set([
  "/",
  "/login",
  "/signup",
  "/terms",
  "/privacy",
  "/accessibility",
  "/admin/submissions",
  "/check-email",
  "/verify-email",
  "/admin-verification",
  "/create/order-summary",
]);

function isScopedPathname(pathname: string): boolean {
  return (
    EXACT_PATHS.has(pathname) ||
    pathname.startsWith("/create/") ||
    pathname.startsWith("/wizard")
  );
}

function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/$/, "");
  return trimmed === "" ? "/" : trimmed;
}

/**
 * Book-creation wizard steps + order summary. In team/product terms these are called "users pages".
 */
export const USER_FLOW_PATHS = new Set([
  "/create/language",
  "/create/story-type",
  "/create/photos-upload",
  "/create/story-details",
  "/create/review",
  "/create/order-summary",
]);

function isUserFlowPath(pathname: string): boolean {
  return USER_FLOW_PATHS.has(normalizePathname(pathname));
}

function FloatingHomeLink({ ariaLabel }: { ariaLabel: string }) {
  return (
    <Link
      to="/"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/90 text-brandNavy shadow-soft backdrop-blur-md transition hover:text-brandOrange focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 sm:h-9 sm:w-9"
      aria-label={ariaLabel}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z" />
        <path d="M9 22V12h6v10" />
      </svg>
    </Link>
  );
}

type Variant = "header" | "fixed";

type Props = {
  variant?: Variant;
  /** Narrower pill for compact headers */
  compact?: boolean;
};

type HeaderStyleLocalePillProps = {
  compact: boolean;
  isHebrewUi: boolean;
  ariaLabel: string;
  onSelectEn: () => void;
  onSelectHe: () => void;
};

function HeaderStyleLocalePill({
  compact,
  isHebrewUi,
  ariaLabel,
  onSelectEn,
  onSelectHe,
}: HeaderStyleLocalePillProps) {
  const btnBase =
    "rounded-full font-bold transition-all flex items-center justify-center " +
    (compact
      ? "h-6 w-[1.3125rem] shrink-0 px-px text-[0.5625rem] leading-none sm:h-7 sm:w-6 sm:text-[0.625rem]"
      : "min-w-[2.25rem] px-2.5 py-1.5 text-xs");
  const btnOn = compact
    ? "bg-headerCta text-white shadow-sm"
    : "bg-headerCta text-white shadow-sm";
  const btnOff = "text-body hover:text-heading";

  return (
    <div
      className={`${compact ? "inline-flex" : "flex"} rounded-full bg-white shadow-sm ring-1 ring-black/5 ${
        compact ? "h-8 items-center gap-px px-0.5 py-0 sm:h-9" : "items-center gap-0.5 p-1"
      }`}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={onSelectEn}
        aria-pressed={!isHebrewUi}
        className={`${btnBase} ${!isHebrewUi ? btnOn : btnOff}`}
      >
        EN
      </button>
      <span
        className={`shrink-0 bg-gray-300 ${compact ? "h-6 w-px sm:h-7" : "mx-0.5 h-4 w-px"}`}
        aria-hidden
      />
      <button
        type="button"
        onClick={onSelectHe}
        aria-pressed={isHebrewUi}
        className={`${btnBase} ${isHebrewUi ? btnOn : btnOff}`}
      >
        עב
      </button>
    </div>
  );
}

const PlatformLanguageSwitcher: React.FC<Props> = ({ variant = "header", compact = false }) => {
  const { pathname } = useLocation();
  const { uiLocale, setUiLocale } = useUiLocale();
  const { t } = useTranslation();

  if (!isScopedPathname(pathname)) {
    return null;
  }

  const isHebrewUi = uiLocale === "he";
  const label = isHebrewUi ? t("platform.switchToEnglish") : t("platform.switchToHebrew");
  const nextLocale = isHebrewUi ? "en" : "he";
  const switchAria = t("platform.languageSwitcherAria");

  const floatingButtonClass =
    "fixed top-4 end-4 z-[45] text-sm sm:text-base font-semibold text-brandNavy bg-white/90 backdrop-blur-md rounded-card px-4 py-2 border border-white/40 shadow-soft hover:text-brandOrange transition focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 rounded";

  if (variant === "header") {
    return (
      <HeaderStyleLocalePill
        compact={compact}
        isHebrewUi={isHebrewUi}
        ariaLabel={switchAria}
        onSelectEn={() => setUiLocale("en")}
        onSelectHe={() => setUiLocale("he")}
      />
    );
  }

  /* variant === "fixed" */
  if (isUserFlowPath(pathname)) {
    return (
      <div className="fixed top-4 end-4 z-[45] flex items-center gap-2" dir="ltr">
        <FloatingHomeLink ariaLabel={t("orderSummary.backHome")} />
        <HeaderStyleLocalePill
          compact
          isHebrewUi={isHebrewUi}
          ariaLabel={switchAria}
          onSelectEn={() => setUiLocale("en")}
          onSelectHe={() => setUiLocale("he")}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={floatingButtonClass}
      onClick={() => setUiLocale(nextLocale)}
      aria-label={switchAria}
    >
      {label}
    </button>
  );
};

export default PlatformLanguageSwitcher;
